import { Link as RouterLink, useParams } from "react-router-dom";
import { BsFacebook } from "react-icons/bs";
import { AiFillLinkedin, AiOutlineTwitter } from "react-icons/ai";
import { useEffect } from "react";
import { Box, Button, Heading, Image, Stack, Tag, Text, Wrap } from "@chakra-ui/react";
import dayjs from "dayjs";
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from "react-share";
import { getPostBySlug } from "../../data/blogPosts";

const SITE_NAME = "Zealight Innovation Labs";
const DEFAULT_TITLE = "Zealight - Websites/Apps Development Company";

function ViewBlogDetails() {
	const { id } = useParams();
	const post = getPostBySlug(id);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [id]);

	// Update the document title and meta description for the article, and restore on leave.
	useEffect(() => {
		const meta = document.querySelector('meta[name="description"]');
		const previousDescription = meta ? meta.getAttribute("content") : null;
		document.title = post ? `${post.title} | ${SITE_NAME}` : `Article not found | ${SITE_NAME}`;
		if (meta && post) meta.setAttribute("content", post.excerpt);
		return () => {
			document.title = DEFAULT_TITLE;
			if (meta && previousDescription !== null) meta.setAttribute("content", previousDescription);
		};
	}, [post]);

	if (!post) {
		return (
			<Box padding={["10rem 2rem", "10rem 6rem"]} textAlign="center">
				<Heading fontFamily={"Trenda-heavy"} fontSize={["2em", "2.5em"]} color="#290F44" mb="20px">
					Article not found.
				</Heading>
				<Text mb="30px">The article you are looking for does not exist or may have moved.</Text>
				<Button as={RouterLink} to="/blog" bg="#290F44" color="#fff" _hover={{ background: "#00FE98", color: "#290F44" }}>
					Back to Blog
				</Button>
			</Box>
		);
	}

	return (
		<Box padding={["7rem 1.5rem 4rem", "9rem 2rem 5rem"]} bg="#FAF8FC">
			<Box data-aos="fade-up" maxW="820px" mx="auto" bg="#fff" borderRadius="24px" boxShadow="0 20px 60px rgba(41,15,68,0.08)" p={["1.5rem", "3rem 3.5rem"]}>
				<Stack direction="row" justifyContent="center" alignItems="center" spacing="12px" mb="16px" flexWrap="wrap">
					<Tag bg="#00FE98" color="#290F44" fontWeight="bold">{post.category}</Tag>
					<Text color="#434144">{dayjs(post.createdAt).format("MMMM DD YYYY")}</Text>
				</Stack>
				<Heading fontFamily={"Trenda-heavy"} fontSize={["1.6em", "2.3em"]} maxW="720px" mx="auto" textAlign="center" lineHeight="1.25" color="#290F44" mb="24px">
					{post.title}
				</Heading>
				<Stack direction={["column", "row"]} alignItems="center" justifyContent="center" spacing={["12px", "30px"]} color="#080010" mb="30px">
					<Text fontWeight="bold">Written by: {post.author}</Text>
					<Stack direction={"row"} alignItems="center">
						<Text>Share:</Text>
						<LinkedinShareButton url={window.location.href}>
							<AiFillLinkedin />
						</LinkedinShareButton>
						<TwitterShareButton url={window.location.href}>
							<AiOutlineTwitter />
						</TwitterShareButton>
						<FacebookShareButton url={window.location.href}>
							<BsFacebook />
						</FacebookShareButton>
					</Stack>
				</Stack>
				<Box>
					<Image loading="lazy" src={post.image} alt={post.imageAlt || post.title} width="100%" maxH="360px" objectFit={"cover"} borderRadius="16px" />
				</Box>

				<Text maxW="700px" mx="auto" my="40px" lineHeight={2} fontSize="1.2em" color="#290F44" fontWeight="500" borderLeft="4px solid #00FE98" pl="20px">
					{post.overview}
				</Text>

				{post.subParagraphs.map((section) => (
					<Stack key={section.title} mb="34px" maxW="700px" mx="auto" spacing="16px">
						<Text as="h2" fontFamily={"Trenda-bold"} fontSize={["1.25em", "1.5em"]} color="#290F44">
							{section.title}
						</Text>
						{section.content.split("\n\n").map((paragraph, index) => (
							<Text key={index} lineHeight={2} color="#2B2530" fontSize="1.05em">
								{paragraph}
							</Text>
						))}
					</Stack>
				))}

				{post.cta && (
					<Box maxW="700px" mx="auto" mt="50px" p={["24px", "36px"]} borderRadius="16px" bg="#290F44" color="#fff">
						<Text lineHeight={2} mb="20px">
							{post.cta.text}
						</Text>
						<Stack direction={["column", "row"]} spacing="15px">
							<Button as={RouterLink} to={post.cta.href} bg="#00FE98" color="#290F44" _hover={{ background: "#fff" }}>
								{post.cta.label}
							</Button>
							<Button as={RouterLink} to="/services" variant="outline" borderColor="#fff" color="#fff" _hover={{ background: "rgba(255,255,255,0.12)" }}>
								Our services
							</Button>
						</Stack>
					</Box>
				)}

				<Wrap mt="50px" maxW="700px" mx="auto" spacing="10px" alignItems="center">
					<Text fontWeight={"bold"}>Tags:</Text>
					{post.tags.map((tag) => (
						<Tag key={tag} bg="#EDE7F4" color="#290F44">
							{tag}
						</Tag>
					))}
				</Wrap>

				<Button as={RouterLink} to="/blog" variant="link" color="#290F44" mt="40px" display="flex" mx="auto" w="fit-content">
					&larr; Back to Blog
				</Button>
			</Box>
		</Box>
	);
}

export default ViewBlogDetails;
